"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, TrendingUp, Calendar, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const valuePoints = ["Executive-level SEO strategy and technical execution focused on commercial revenue", "Save £80,000–£120,000/yr compared to a full-time director salary", "Flexible senior leadership tailored to your growth phase", "Clear technical audits, content architecture, and revenue attribution"];
const highlights = [{
  label: "Director-level guidance",
  detail: "Without £100k+ overhead"
}, {
  label: "Hands-on implementation",
  detail: "Technical & commercial focus"
}, {
  label: "Lean cost structure",
  detail: "No recruiter or hiring fees"
}];
export default function FractionalHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        duration: 0.5
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="herofractionalseo" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-12 pb-20 md:pt-16 md:pb-28 lg:pt-20 lg:pb-32 border-b border-border">
      {/* Subtle decorative background tint */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,hsl(220_80%_30%/0.04),transparent_60%)]" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-secondary/40 blur-3xl -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          
          {/* Left Column: Main Copy */}
          <motion.div variants={containerVariants} initial="hidden" animate="show" className="lg:col-span-7 flex flex-col justify-center text-left">
            {/* Tag / Badge */}
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="outline" className="px-3.5 py-1 text-xs font-semibold tracking-wide border-primary/25 bg-muted/60 text-primary uppercase font-mono rounded-full inline-flex items-center gap-2">
                <UserCheck className="size-3.5 text-primary" />
                <span>Fractional SEO Leadership</span>
              </Badge>
            </motion.div>

            {/* Exact H1 */}
            <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif tracking-tight leading-[1.15] text-foreground text-balance mb-6">
              Strategic SEO Leadership Without Full-Time Hiring Costs
            </motion.h1>

            {/* Subtitle / Lead Body */}
            <motion.p variants={itemVariants} className="text-base sm:text-lg md:text-xl text-muted-foreground font-sans leading-relaxed text-balance mb-6">
              As your independent <strong className="text-foreground font-semibold">fractional SEO consultant</strong>, I deliver senior SEO leadership, strategic direction, and measurable organic revenue growth for UK and international brands — without the £80,000–£120,000 annual salary, recruitment fees, and overhead of a full-time director.
            </motion.p>

            {/* Value checklist */}
            <motion.div variants={itemVariants} className="space-y-2.5 mb-8">
              {valuePoints.map((point, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                  <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-foreground font-sans leading-snug">
                    {point}
                  </span>
                </div>)}
            </motion.div>

            {/* CTA Group */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 sm:gap-4 mb-8">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 py-3.5 h-auto text-base rounded-md shadow-sm transition-[background-color,box-shadow]">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted font-medium px-6 py-3.5 h-auto text-base text-foreground rounded-md transition-[background-color,border-color]">
                <Link to="/SeoAuditsPage" className="inline-flex items-center justify-center gap-2">
                  <TrendingUp className="size-4 text-primary" />
                  <span>Get a Free SEO Audit</span>
                </Link>
              </Button>
            </motion.div>

            {/* Trust Micro-Note */}
            <motion.div variants={itemVariants} className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground pt-3 border-t border-border/70">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>
                Fractional SEO leadership with Yassine Essaadi — direct senior partnership delivering strategic organic growth at a fraction of full-time cost.
              </span>
            </motion.div>
          </motion.div>

          {/* Right Column: Visual Portrait Card & Commercial Comparison */}
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          scale: shouldReduceMotion ? 1 : 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.15
        }} className="lg:col-span-5 relative flex flex-col items-center">
            {/* Main Portrait Frame Card */}
            <div className="w-full max-w-md lg:max-w-none bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-md relative">
              
              {/* Image Frame */}
              <div className="relative aspect-4/5 w-full rounded-xl overflow-hidden bg-muted border border-border/80 mb-6">
                <img src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" alt="Yassine Essaadi, Fractional SEO Consultant and Strategy Specialist" className="w-full h-full object-cover object-top" loading="eager" decoding="async" />

                {/* Floating Status Pill on image */}
                <div className="absolute top-3 left-3 bg-background/95 backdrop-blur-xs border border-border text-foreground px-3 py-1.5 rounded-full text-xs font-medium shadow-xs flex items-center gap-2">
                  <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span>Available for Q2/Q3 Fractional Roles</span>
                </div>
              </div>

              {/* Consultant Identity & Direct Value Callout */}
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
                  <div>
                    <h2 className="font-serif font-bold text-lg text-foreground">
                      Yassine Essaadi
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground font-sans">
                      Independent Senior SEO Consultant
                    </p>
                  </div>
                  <Badge variant="secondary" className="font-mono text-xs px-2.5 py-1 text-primary bg-secondary font-medium">
                    YE Consultancy
                  </Badge>
                </div>

                {/* Mini Stat Summary */}
                <div className="grid grid-cols-3 gap-2 text-center pt-1">
                  {highlights.map((item, index) => <div key={index} data-index={index} className="p-2 rounded-lg bg-muted/50 border border-border/60">
                      <div className="text-xs font-semibold text-foreground font-serif leading-tight">
                        {item.label}
                      </div>
                      <div className="text-[13px] text-muted-foreground mt-0.5 leading-tight">
                        {item.detail}
                      </div>
                    </div>)}
                </div>
              </div>

            </div>

            {/* Bottom floating comparison hint */}
            <div className="w-full max-w-md lg:max-w-none mt-3.5 px-3 py-2.5 rounded-lg bg-secondary/50 border border-secondary text-secondary-foreground text-xs flex items-center justify-between">
              <span className="font-medium">Full-time director: £80k-£120k + benefits</span>
              <span className="text-primary font-semibold">Fractional: fraction of the cost</span>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}